import { useRef, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 12 — Accent Arpeggios with Tresillo (3-3-2).
 *
 * Three standard arpeggio patterns (LMHM, HMLM, HLML) on a I-vi-IV-V
 * progression (B♭ and D major in the video). Then the tresillo arpeggios from
 * Jason's chart: L-M-H, L-M-H, L-M (or H-M-L, H-M-L, H-M) — the pitch pattern
 * itself groups the eight eighths 3+3+2, the shape behind Coldplay's "Clocks".
 *
 * Bespoke visual: the Arpeggio Carousel — a strip showing the active
 * arpeggio shape as a curve over the chord tones, with the 3-3-2 accent
 * cells highlighted underneath.
 */

interface ArpeggioPattern {
  id: string;
  name: string;
  description: string;
  /** Indices into chord [low, mid, high] for each of the 3 notes in a triplet OR each of the 8 eighths in a bar. */
  cells: number[]; // 8-cell pattern across one bar — 8 eighth notes
}

// 8 cells per bar (EIGHTHS — 8 eighths fill one 4/4 bar). Each cell is index into [L=0, M=1, H=2] of the chord.
const PATTERNS: ArpeggioPattern[] = [
  { id: "lmhm",       name: "L-M-H-M",         description: "Low-middle-high-middle. The classic stair-up flow.",   cells: [0, 1, 2, 1, 0, 1, 2, 1] },
  { id: "hmlm",       name: "H-M-L-M",         description: "Reverse — top-down. Heavy, ballad-like.",            cells: [2, 1, 0, 1, 2, 1, 0, 1] },
  { id: "hlml",       name: "H-L-M-L",         description: "High-low-middle-low.",                               cells: [2, 0, 1, 0, 2, 0, 1, 0] },
  { id: "tres-lmh",   name: "L-M-H · L-M-H · L-M", description: "Tresillo arpeggio: the low note comes back on eighths 1, 4 and 7.", cells: [0, 1, 2, 0, 1, 2, 0, 1] },
  { id: "tres-hml",   name: "H-M-L · H-M-L · H-M", description: "Tresillo from the top — the shape of the Clocks riff.",          cells: [2, 1, 0, 2, 1, 0, 2, 1] },
];

// 3-3-2 accent positions (cells 0, 3, 6)
const ACCENT_3_3_2 = [0, 3, 6];

export default function PianoIntermediate12() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "12");
  return (
    <LessonShell course={course} courseSlug="piano-intermediate" lessonOrder={lesson.order} title={lesson.title} description={lesson.desc} videoUrl={lesson.video} thumbUrl={lesson.thumb} accent={accent}>
      <KeyProvider defaultTonic="Bb" availableQualities={["major"]}>
        <PIShell title="Accent Arpeggios with Tresillo (3-3-2)" exerciseGoal="Three standard arpeggio shapes, then the tresillo arpeggio — the same chord tones grouped 3+3+2." bpm={92} difficulty="Advanced">
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [patternIdx, setPatternIdx] = useState(3);
  const [accentOn, setAccentOn] = useState(true);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);
  const pattern = PATTERNS[patternIdx];

  // I-vi-IV-V progression
  const progression = [0, 9, 5, 7]; // semitones from tonic for major

  const stop = () => { handleRef.current?.stop(); handleRef.current = null; setRunning(false); setStep(-1); };

  const play = async () => {
    kickAudio();
    stop();
    setRunning(true);
    const totalSteps = 32; // 4 bars × 8 eighths
    const events: { beat: number; notes: string[]; durationSteps: number; velocity?: number }[] = [];
    for (let bar = 0; bar < 4; bar++) {
      const rootSemis = progression[bar];
      const root = tonicMidi + rootSemis;
      // vi is a MINOR chord (G minor in B♭); I, IV and V are major.
      const triad = [root, root + (bar === 1 ? 3 : 4), root + 7];
      // Jason inverts so the first (thumb) note isn't a black key: take the
      // first position, root upward, whose lowest note is white.
      const isBlack = (m: number) => [1, 3, 6, 8, 10].includes(((m % 12) + 12) % 12);
      const positions = [triad, [triad[1], triad[2], triad[0] + 12], [triad[2], triad[0] + 12, triad[1] + 12]];
      const chordTones = positions.find((p) => !isBlack(p[0])) ?? triad;
      pattern.cells.forEach((toneIdx, j) => {
        const stepIdx = bar * 8 + j;
        const isAccent = accentOn && ACCENT_3_3_2.includes(j);
        events.push({
          beat: stepIdx,
          notes: [midiToPitchName(chordTones[toneIdx], k.useFlats)],
          durationSteps: 1,
          velocity: isAccent ? 0.95 : 0.5,
        });
      });
      // LH bass on beat 1 of each bar
      events.push({ beat: bar * 8, notes: [midiToPitchName(root - 12, k.useFlats)], durationSteps: 8, velocity: 0.7 });
    }
    handleRef.current = await playRhythm(events, {
      bpm: 92, subdivide: 2, totalSteps, loop: true, click: true, beatsPerBar: 4,
      onStep: (s) => setStep(s),
    });
  };

  const masterHighlights: KeyHighlight[] = [
    { degree: 1, tone: "root", label: "I" },
    { degree: 6, tone: "voice", label: "vi" },
    { degree: 4, tone: "voice", label: "IV" },
    { degree: 5, tone: "voice", label: "V" },
  ];

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram highlights={masterHighlights} octaves={2} startOctave={3} playable />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          The four chord roots of the I-vi-IV-V cycle in the current key.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Same notes. Different accent. Different song.">
          <p>
            Take three notes of a chord and play them in some order eight times across a bar.
            That's an arpeggio. <strong>Now group the eight 3+3+2</strong>: low-middle-high,
            low-middle-high, low-middle. The low note comes back on the 1st, 4th and 7th eighth,
            and that repetition creates the accent — the tresillo. Same notes, totally different
            feel.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "So that low note repetition is creating that irregularity and that irregularity is
            what we call as an accent."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="Five shapes" title="Three standard, two tresillo">
          <p>
            The three standard arpeggios on Jason's chart — L-M-H-M, H-M-L-M and H-L-M-L — group
            the eighths evenly. The two tresillo arpeggios group them 3+3+2. Pick one below — the
            carousel will redraw the curve and the active cells. Jason points out that H-M-L,
            H-M-L, H-M is exactly the arpeggio of Coldplay's "Clocks". Whatever the shape, hold
            the first note of each group so it rings on.
          </p>
          <div className="my-3 grid grid-cols-1 gap-2 md:grid-cols-3">
            {PATTERNS.map((p, i) => (
              <motion.button key={p.id} layout onClick={() => { stop(); setPatternIdx(i); }}
                className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                  i === patternIdx ? "border-amber-400/60 bg-amber-500/10" : "border-cyan-400/20 bg-cyan-500/5 hover:bg-cyan-500/15"
                }`}>
                <div className={`text-[14px] font-bold ${i === patternIdx ? "text-amber-200" : "text-cyan-100"}`}>{p.name}</div>
                <div className="mt-1 text-[11px] leading-snug text-cyan-300/80">{p.description}</div>
              </motion.button>
            ))}
          </div>
          <ArpeggioCarousel pattern={pattern} accentOn={accentOn} step={running ? step % 8 : -1} />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button onClick={play} disabled={running} className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50">▶ Loop the I-vi-IV-V cycle</button>
            <label className="flex select-none items-center gap-1.5 text-[12px] text-cyan-200">
              <input type="checkbox" checked={accentOn} onChange={(e) => setAccentOn(e.target.checked)} className="h-3.5 w-3.5 accent-amber-400" />
              3-3-2 accent on
            </label>
            {running && <button onClick={stop} className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20">■ Stop</button>}
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Why it grooves" title="The accent is the song">
          <p>
            With the accent OFF, the standard patterns are a finger drill — every cell at equal
            volume. Toggle the accent ON to lean on eighths 1, 4 and 7 as well. On the tresillo
            shapes the grouping is already in the notes; the extra weight just makes it obvious.
            You can also put the accent in the left hand by playing the 3+3+2 hits there.
          </p>
          <PIInsight>
            <p>
              <strong>The accent is the song.</strong> Same notes, a different grouping of the
              eighths — different music.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Inversions matter" title="Keep a white key under your thumb">
          <p>
            All three patterns work in any inversion of the chord. But the SHAPE of your hand
            changes radically depending on which inversion you choose. <strong>Avoid putting a
            black key under your thumb on the bottom note</strong> — your thumb can't easily slide
            inside the keyboard. Re-invert the chord so the lowest note is white whenever
            possible — in B♭, Jason re-inverts B♭ major so the black note moves away from the
            thumb, where the ring finger can take it. The player above does the same. And in the left hand, always play the
            true root of the chord.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Variations" title="3-2-3, 2-3-3 — same maths, new flavours">
          <p>
            3+3+2 isn't the only way to total 8. You can also do <strong>3+2+3</strong> or{" "}
            <strong>2+3+3</strong>. Each adds up to 8 eighths but distributes the accents
            differently — different syncopation, different musical mood. In konnakol, Jason counts
            a 3 as "ta-ki-ta" and a 2 as "ta-ka".
          </p>
          <PIQuote attribution="Jason · lesson video">
            "You might argue that even 3 plus 2 plus 3 also equals to 8 so you could do that
            variation of the beats."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="The cycle in your chosen key">
          <LessonMidi courseSlug="piano-intermediate" order="12" label={`Tresillo arpeggios · ${k.tonicName}`} originalBpm={92} />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={<>Pattern at a time. The accent is added last so your fingers learn the shape before they learn the groove.</>}
            rungs={[
              { title: "L-M-H-M without accent",      minutes: 4, accent: "amber",   detail: "Equal velocity. Eighth notes. Get the shape under your fingers across the four chords." },
              { title: "Add the 3-3-2 accent",         minutes: 4, accent: "cyan",    detail: "Same shape. Now stress eighths 1, 4, 7. Listen for the groove that emerges." },
              { title: "Tresillo arpeggios",          minutes: 4, accent: "rose",    detail: "L-M-H · L-M-H · L-M, then H-M-L · H-M-L · H-M (the Clocks shape). The grouping lives in the notes." },
              { title: "Try 3-2-3 placement",          minutes: 4, accent: "violet",  detail: "Move the accent: stress eighths 1, 4, 6. Same total of 8 — different syncopation." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

function ArpeggioCarousel({ pattern, accentOn, step }: { pattern: ArpeggioPattern; accentOn: boolean; step: number }) {
  const heights = [22, 50, 80]; // for L=0, M=1, H=2 visualisation
  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-3">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">⌒ Arpeggio · {pattern.name}</div>
        <div className="font-mono text-[11px] text-cyan-300/80">{accentOn ? "3-3-2 accent ON" : "even eighths"}</div>
      </div>
      {/* Curve / cell row */}
      <div className="flex h-24 items-end gap-1">
        {pattern.cells.map((toneIdx, i) => {
          const isAccent = accentOn && ACCENT_3_3_2.includes(i);
          const isActive = step === i;
          return (
            <motion.div key={i}
              animate={{ scale: isActive ? 1.12 : 1, boxShadow: isActive ? "0 0 0 2px #FCD34D" : "0 0 0 0 transparent" }}
              transition={{ duration: 0.1 }}
              className={`flex-1 rounded-t-md ${
                isAccent ? "bg-amber-400" : toneIdx === 0 ? "bg-cyan-700" : toneIdx === 1 ? "bg-cyan-500" : "bg-cyan-300"
              }`}
              style={{ height: `${heights[toneIdx]}%` }}>
              <div className="flex h-full items-start justify-center pt-1 font-mono text-[10px] font-bold text-zinc-900/90">
                {toneIdx === 0 ? "L" : toneIdx === 1 ? "M" : "H"}
              </div>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-1 grid grid-cols-8 gap-1 text-center text-[10px] uppercase tracking-wider text-cyan-300/60">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((n, i) => (
          <span key={i} className={ACCENT_3_3_2.includes(i) && accentOn ? "font-bold text-amber-300" : ""}>{n}</span>
        ))}
      </div>
    </div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (<motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>{children}</motion.div>);
}
function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>{children}</blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
